/** @jsxImportSource @emotion/react */
import { useTranslation } from "react-i18next"
import { withMyTheme } from "../../theme/theme"
import { css, Theme } from "@emotion/react"
import { mobileCss } from "../../theme/isMobile"
import { MyButton } from "../../components/button/MyButton"
import { OldConfig } from "../../../config/OldConfig"
import { openUrl } from "../../util/openUrl"
import { TextSizes } from "../../util/textSizes"
import PsychologyIcon from '@mui/icons-material/Psychology'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import CelebrationIcon from '@mui/icons-material/Celebration'

export const ATTRACTIONS_ID = 'attractions'

const AttractionsContentStyle = withMyTheme((theme) => css`
    background-image: url('/images/peach_background_30.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 75vh;
    width: 100vw;
    padding: 5rem 0;
    color: ${theme.palette.text.primary};
    ${mobileCss(css`
        height: auto;
    `)}
`)

const AttractionsTitleStyle = withMyTheme((theme) => css`
    font-size: ${TextSizes.PAGE_TITLE};
    font-family: ${theme.typography.h1.fontFamily};
    color: ${theme.palette.primary.main};
    margin-bottom: 3rem;
    text-align: center;
    ${mobileCss(css`
        font-size: clamp(2rem, 8vw, 3rem);
        margin-bottom: 2rem;
    `)}
`)

const AttractionsGridStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 5vh;
    justify-content: center;
    ${mobileCss(css`
        flex-direction: column;
        gap: 1.5rem;
    `)}
`)

const AttractionCardStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background: ${theme.palette.background.paper};
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    width: 25vw;
    padding: 2rem;
    text-align: center;
    gap: 1rem;
   ${mobileCss(`
        width: 75vw;
        padding: 5vw;
    `)}
`)

const AttractionCardTitleStyle = withMyTheme((theme) => css`
    font-size: 3.5vh;
    font-weight: 700;
    color: ${theme.palette.primary.main};
    font-family: ${theme.typography.h1.fontFamily};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`)

const IconStyle = withMyTheme(() => css`
    font-size: inherit;
    vertical-align: middle;
`)

const AttractionCardDescriptionStyle = withMyTheme((theme) => css`
    font-size: 2vh;
    color: ${theme.palette.text.primary};
    font-family: ${theme.typography.body1.fontFamily};
    line-height: 1.6;
    margin-bottom: 1rem;
    white-space: pre-line;
`)

const AttractionCardFooterStyle = withMyTheme((theme) => css`
    font-size: 0.9rem;
    color: ${theme.palette.text.secondary};
    font-family: ${theme.typography.body1.fontFamily};
    font-style: italic;
`)

const QuizButtonStyle = withMyTheme(() => css`
    background-color: #d8b8ad;
    color: white;
    &:hover {
        background-color: #c9a79d;
    }
`)

interface AttractionCardProps {
    title: string;
    icon: React.ReactNode;
    description: string;
    footer?: string;
    buttonText?: string;
    onButtonClick?: () => void;
    disabled?: boolean;
}

const AttractionCard = ({ title, icon, description, footer, buttonText, onButtonClick, disabled }: AttractionCardProps) => {
    return (
        <div css={AttractionCardStyle}>
            <div css={AttractionCardTitleStyle}>
                {icon}
                <span>{title.replace(/\s*[\uD800-\uDBFF][\uDC00-\uDFFF]\s*/, '')}</span>
            </div>
            <div css={AttractionCardDescriptionStyle} dangerouslySetInnerHTML={{ __html: description }} />
            {buttonText && onButtonClick && (
                <MyButton
                    text={buttonText}
                    variant="contained"
                    onClick={onButtonClick}
                    additionalCss={QuizButtonStyle}
                    disabled={disabled}
                />
            )}
            {footer && <div css={AttractionCardFooterStyle}>{footer}</div>}
        </div>
    )
}

export const Attractions = () => {
    const { t } = useTranslation()

    const isQuizEnabled = () => {
        const quizStartDate = new Date('2025-07-07')
        const currentDate = new Date()
        return currentDate >= quizStartDate
    }

    const attractions = [
        {
            title: t('attractions.quiz.title'),
            icon: <PsychologyIcon css={IconStyle} />,
            description: t('attractions.quiz.description'),
            buttonText: t('attractions.quiz.button'),
            onButtonClick: () => { openUrl(OldConfig.attractions.quizUrl) },
            disabled: !isQuizEnabled()
        },
        {
            title: t('attractions.bingo.title'),
            icon: <VisibilityIcon css={IconStyle} />,
            description: t('attractions.bingo.description'),
            footer: t('attractions.bingo.footer')
        },
        {
            title: t('attractions.guestBook.title'),
            icon: <AutoStoriesIcon css={IconStyle} />,
            description: t('attractions.guestBook.description'),
            footer: t('attractions.guestBook.footer')
        }
    ]

    return (
        <section id={ATTRACTIONS_ID} css={AttractionsContentStyle}>
            <h2 css={AttractionsTitleStyle}>{t('attractions.title')}</h2>
            <div css={AttractionsGridStyle}>
                {attractions.map((attraction, index) => (
                    <AttractionCard
                        key={index}
                        title={attraction.title}
                        icon={attraction.icon}
                        description={attraction.description}
                        footer={attraction.footer}
                        buttonText={attraction.buttonText}
                        onButtonClick={attraction.onButtonClick}
                        disabled={attraction.disabled}
                    />
                ))}
            </div>
        </section>
    )
} 
